import { Injectable } from '@angular/core';
import { TaxillaApiService } from '@encomply/core/services/taxillaapi/taxillaapi.service';
import { translate } from '@ngneat/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { from, of } from 'rxjs';
import { catchError, delay, map, mergeMap, withLatestFrom } from 'rxjs/operators';

import { Chain } from '../../models/chain.interface';
import { Transformation } from '../../models/transformation';
import { ApiService } from '../../services/api/api.service';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { UtilsService } from '../../services/utils/utils.service';
import {
    AlertError,
    AlertSuccess,
    GetActiveTransformationsByType,
    GetAllTransformations,
    GetAppDetailsThroughApi,
    GetAssetWithLatestVersion,
    GetChainsAndTransformation,
    GetInboundDetailedTransformations,
    GetIntegrationsChains,
    GetMasterSchedulerTransformations,
    GetOutboundChains,
    GetTransformationNamesInChain,
    SetActiveTransformationsByType,
    SetChainDisplayNames,
    SetInboundDetailedTransformations,
    SetIntegrationsChains,
    SetMasterSchedulerTransformations,
    SetOutboundChains,
    SetTransformationAndChains,
    SetTransformationNamesInChain,
    SetTransformations,
    SetTransformationsLoading,
    SetTransformationsRelativeOrganizations,
    SetUpdatedTransformations,
    UpdateActiveTransformationsByType,
} from '../actions';
import {
    getAllApps$,
    getAllServicesMap$,
    getAllTransformationsServicesMap$,
    getAssetsWithLatestVersion$,
    getCurrentLocationIdsHierarchy$,
    getCurrentOrganizationId$,
    getOutboundIntegrationsChains$,
    getServicesApiMap$,
    getSubscribedAppsMap$,
} from '../selectors';

@Injectable()
export class TransformationEffects {
    private loadingChains: { [key: string]: boolean } = {};
    private loadingActiveTransformations: { [key: string]: boolean } = {};

    constructor(
        private actions$: Actions,
        private _api: ApiService,
        private _taxilla: TaxillaApiService,
        private store$: Store,
        private _commonUtils: CommonUtilsService
    ) {}

    getInboundDetailedTransformations = createEffect(() =>
        this.actions$.pipe(
            ofType(GetInboundDetailedTransformations),
            delay(10),
            withLatestFrom(
                this.store$.select(getAllTransformationsServicesMap$),
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getAssetsWithLatestVersion$)
            ),
            mergeMap(([action, transformationServicesMap, organizationId, versionsMap]) => {
                const serviceMap = transformationServicesMap?.[action.restApiName];
                const loading = serviceMap?.loading?.inbound;
                if (Object.keys(serviceMap?.transformations?.inboundDetailed || {})?.length > 0 && !action.forceFetch) {
                    return [];
                }
                if (!versionsMap?.[action.serviceId]?.metadataId) {
                    this.store$.dispatch(
                        GetAssetWithLatestVersion({
                            serviceId: action.serviceId,
                        })
                    );
                    setTimeout(() => {
                        this.store$.dispatch(GetInboundDetailedTransformations(action));
                    }, 500);
                    return [];
                }
                const assetId = action.assetMetaUid || versionsMap?.[action.serviceId]?.metadataId;
                if (!assetId || loading) {
                    return [];
                }
                this.store$.dispatch(
                    SetTransformationsLoading({
                        category: 'inbound',
                        loading: true,
                        organizationId: organizationId,
                        restApiName: action.restApiName,
                        serviceId: action?.serviceId,
                    })
                );
                return from(
                    this._api.assets.getAllMapTransformations({
                        assetMetaUId: assetId,
                        noAlerts: action.noAlerts,
                    })
                ).pipe(
                    map(res => {
                        this.store$.dispatch(
                            SetTransformationsLoading({
                                category: 'inbound',
                                loading: false,
                                organizationId: organizationId,
                                restApiName: action.restApiName,
                                serviceId: action.serviceId,
                            })
                        );
                        const transformations: any = res.transformations;
                        this.mapTransformationSources(transformations, res?.transformationSources, res?.chainNameVsChainDisplayName);
                        const relativeOrganizations = res.organizations;
                        return SetInboundDetailedTransformations({
                            restApiName: action.restApiName,
                            organizationId,
                            inbound: res.transformations,
                            relativeOrganizations,
                        });
                    }),
                    catchError(e => {
                        this.store$.dispatch(
                            SetTransformationsLoading({
                                category: 'inbound',
                                loading: false,
                                organizationId: organizationId,
                                restApiName: action.restApiName,
                                serviceId: action.serviceId,
                            })
                        );
                        return of(AlertError({ message: e }));
                    })
                );
            })
        )
    );

    private mapTransformationSources = (orgMap, transformationSources, chainNames: { [property: string]: string }) => {
        Object.keys(orgMap || {}).forEach(orgId => {
            Object.keys(orgMap[orgId]).forEach(chainName => {
                const transformation = orgMap?.[orgId]?.[chainName];
                const transformationName = transformation?.name;
                transformation.id = UtilsService.guid();
                transformation.chainName = chainName;
                transformation.chainDisplayName = chainNames?.[chainName];
                transformation.displayName = transformation.displayName || transformation.name;
                transformation.isAssetToAsset =
                    transformation.sourceParts?.find(sourcePart => sourcePart.sourceType === 'ASSET') &&
                    transformation.assetToAssetMetadata !== undefined;
                transformation.isSearchAsset =
                    transformation.sourceParts?.find(sourcePart => sourcePart.sourceType === 'ASSET') &&
                    transformation.assetToAssetMetadata === undefined;
                transformation.isFileUpload = !!transformation.sourceParts?.find(
                    sourcePart => sourcePart.sourceType === 'FILE_UPLOAD' || sourcePart.sourceType !== 'ASSET'
                );
                const sourceDetails = transformationSources?.[orgId]?.[transformationName];
                if (!sourceDetails) {
                    return;
                }
                orgMap[orgId][chainName]?.sourceParts?.forEach(sourcePart => {
                    sourcePart.appDetails = sourceDetails[sourcePart.name];
                });
            });
        });
    };

    getChainNames = createEffect(() =>
        this.actions$.pipe(
            ofType(GetInboundDetailedTransformations),
            delay(10),
            withLatestFrom(
                this.store$.select(getAllTransformationsServicesMap$),
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getAssetsWithLatestVersion$)
            ),
            mergeMap(([action, transformationServicesMap, organizationId, versionsMap]) => {
                const serviceMap = transformationServicesMap?.[action.serviceId];
                const loading = serviceMap?.loading?.chainNames;
                if (serviceMap?.chainDisplayNames && !action.forceFetch) {
                    return [];
                }
                if (loading) {
                    return [];
                }
                if (!versionsMap?.[action.serviceId]?.metadataId) {
                    this.store$.dispatch(
                        GetAssetWithLatestVersion({
                            serviceId: action.serviceId,
                        })
                    );
                    setTimeout(() => {
                        this.store$.dispatch(GetInboundDetailedTransformations(action));
                    }, 500);
                    return [];
                }
                this.store$.dispatch(
                    SetTransformationsLoading({
                        category: 'chainNames',
                        loading: true,
                        organizationId: organizationId,
                        restApiName: action.restApiName,
                        serviceId: action.serviceId,
                    })
                );
                return from(
                    this._api.assets.getChainDisplayNames({
                        restApiName: action.restApiName,
                        assetId: action.assetMetaUid || versionsMap?.[action.serviceId]?.metadataId,
                        noAlerts: action.noAlerts,
                    })
                ).pipe(
                    map(res => {
                        this.store$.dispatch(
                            SetTransformationsLoading({
                                category: 'chainNames',
                                loading: false,
                                organizationId: organizationId,
                                restApiName: action.restApiName,
                                serviceId: action.serviceId,
                            })
                        );
                        return SetChainDisplayNames({
                            restApiName: action.restApiName,
                            organizationId,
                            chainVsDisplaynames: res?.chainNameVsChainDisplayName,
                        });
                    }),
                    catchError(e => {
                        this.store$.dispatch(
                            SetTransformationsLoading({
                                category: 'chainNames',
                                loading: false,
                                organizationId: organizationId,
                                restApiName: action.restApiName,
                                serviceId: action.serviceId,
                            })
                        );
                        return of(AlertError({ message: e }));
                    })
                );
            })
        )
    );

    setChainNames = createEffect(() =>
        this.actions$.pipe(
            ofType(SetChainDisplayNames, SetInboundDetailedTransformations),
            delay(10),
            withLatestFrom(this.store$.select(getAllTransformationsServicesMap$), this.store$.select(getCurrentOrganizationId$)),
            mergeMap(([action, transformationsServicesMap, organizationId]) => {
                const service = transformationsServicesMap?.[action.serviceId];
                if (Object.keys(service?.chainDisplayNames || {})?.length > 0 || !service?.chainDisplayNames) {
                    return [];
                }
                const chainNames = service.chainDisplayNames;
                const newMap = CommonUtilsService.cloneObject(service);
                const inBoundTransformations = newMap.transformations?.inBoundTransformations;
                const { relativeOrganizations } = service;
                inBoundTransformations?.transformations?.forEach(transformation => {
                    transformation.displayName = transformation.displayName || chainNames[transformation.id || transformation.name];
                });
                return of(
                    SetUpdatedTransformations({
                        restApiName: action.restApiName,
                        organizationId,
                        inbound: inBoundTransformations,
                        relativeOrganizations,
                    })
                );
            })
        )
    );

    getAllTransformations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetAllTransformations),
            withLatestFrom(
                this.store$.select(getSubscribedAppsMap$),
                this.store$.select(getAllTransformationsServicesMap$),
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getAssetsWithLatestVersion$),
                this.store$.select(getAllServicesMap$)
            ),
            mergeMap(([action, appsMap, services, organizationId, versionsMap, allServicesMap]) => {
                const app = appsMap?.[action.serviceId] || allServicesMap?.[action.serviceId];
                const transformations = services?.[action.restApiName]?.transformations;
                const loading = services?.[action.restApiName]?.loading?.outbound;
                if (((transformations?.outbound?.length > 0 && transformations?.inbound?.length > 0) || loading) && !action.forceFetch) {
                    return [];
                }
                if (!versionsMap?.[action.serviceId]?.metadataId) {
                    this.store$.dispatch(
                        GetAssetWithLatestVersion({
                            serviceId: action.serviceId,
                        })
                    );
                    setTimeout(() => {
                        this.store$.dispatch(GetAllTransformations(action));
                    }, 500);
                    return [];
                }
                this.store$.dispatch(
                    SetTransformationsLoading({
                        category: 'outbound',
                        loading: true,
                        organizationId: organizationId,
                        restApiName: action.restApiName,
                        serviceId: action.serviceId,
                    })
                );
                return from(
                    this._api.assets.getAllTransformations({
                        assetMetaUId: app?.assetMetaUId || versionsMap?.[action.serviceId]?.metadataId,
                        assetName: app?.serviceName || versionsMap?.[action.serviceId]?.metadataName,
                        noAlerts: action?.noAlerts,
                    })
                ).pipe(
                    map((res: any) => {
                        this.store$.dispatch(
                            SetTransformationsLoading({
                                category: 'outbound',
                                loading: false,
                                organizationId: organizationId,
                                restApiName: action.restApiName,
                                serviceId: action.serviceId,
                            })
                        );
                        const allTransformations: { INBOUND: Transformation[]; OUTBOUND: Transformation[] } = {
                            INBOUND: (res?.response?.allTransformations?.['INBOUND'] as Transformation[]) || [],
                            OUTBOUND: (res?.response?.allTransformations?.['OUTBOUND'] as Transformation[]) || [],
                        };
                        const providerOrgId = this._commonUtils.getFromStorage('currentOrganization')?.providerOrganizationId;
                        allTransformations.INBOUND.forEach(transformation => {
                            const rawName = res?.response?.organizations?.[transformation.repositoryId];
                            transformation.organizationName =
                                providerOrgId && transformation.repositoryId === providerOrgId ? 'Default' : rawName;
                        });

                        allTransformations.OUTBOUND.forEach(transformation => {
                            const rawName = res?.response?.organizations?.[transformation.repositoryId];
                            transformation.organizationName =
                                providerOrgId && transformation.repositoryId === providerOrgId ? 'Default' : rawName;
                        });
                        this.store$.dispatch(
                            SetTransformationsRelativeOrganizations({
                                restApiName: action.restApiName,
                                organizationId,
                                relativeOrganizations: res?.response?.organizations,
                            })
                        );
                        return SetTransformations({
                            restApiName: action.restApiName,
                            organizationId,
                            transformations: allTransformations,
                        });
                    }),
                    catchError(e => {
                        this.store$.dispatch(
                            SetTransformationsLoading({
                                category: 'outbound',
                                loading: false,
                                organizationId: organizationId,
                                restApiName: action.restApiName,
                                serviceId: action.serviceId,
                            })
                        );
                        return of(AlertError({ message: e.msg }));
                    })
                );
            })
        )
    );

    getActiveTransformationsByType$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetActiveTransformationsByType),
            withLatestFrom(
                this.store$.select(getSubscribedAppsMap$),
                this.store$.select(getAllTransformationsServicesMap$),
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getAllServicesMap$),
                this.store$.select(getAllApps$)
            ),
            mergeMap(([action, appsMap, subscribedServices, organizationId, allServicesMap, allApps]) => {
                const app =
                    appsMap?.[action.serviceId] ||
                    allServicesMap?.[action.serviceId] ||
                    allApps?.find(app => app.serviceId === action.serviceId);
                const transformations = subscribedServices?.[action.restApiName]?.transformations;
                if (
                    ((action.transformationType === 'INBOUND' && transformations?.activeTransformations?.inbound?.length > 0) ||
                        (action.transformationType === 'OUTBOUND' && transformations?.activeTransformations?.outbound?.length > 0)) &&
                    !action.forceFetch
                ) {
                    return [];
                }
                if (this.loadingActiveTransformations[`${action.transformationType}|${app?.restApiName}`]) {
                    return [];
                }
                this.loadingActiveTransformations[`${action.transformationType}|${app?.restApiName}`] = true;
                return from(
                    this._api.assets.getActiveTransformationsByType({
                        transformationType: action.transformationType,
                        assetRestAPIName: app?.restApiName,
                        noAlerts: action?.noAlerts,
                    })
                ).pipe(
                    map((res: any) => {
                        this.loadingActiveTransformations[`${action.transformationType}|${app?.restApiName}`] = false;
                        return SetActiveTransformationsByType({
                            restApiName: action.restApiName,
                            organizationId,
                            transformationNames: (res.response.transformations as string[]) || [],
                            transformationType: action.transformationType,
                        });
                    }),
                    catchError(e => {
                        this.loadingActiveTransformations[`${action.transformationType}|${app?.restApiName}`] = false;
                        if (!action.noAlerts) {
                            this.store$.dispatch(AlertError({ message: e.msg }));
                        }
                        return of(
                            SetActiveTransformationsByType({
                                restApiName: action.restApiName,
                                organizationId,
                                transformationNames: [],
                                transformationType: action.transformationType,
                            })
                        );
                    })
                );
            })
        )
    );

    updateActiveTransformationsByType$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpdateActiveTransformationsByType),
            withLatestFrom(
                this.store$.select(getSubscribedAppsMap$),
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getAllTransformationsServicesMap$),
                this.store$.select(getAssetsWithLatestVersion$),
                this.store$.select(getAllServicesMap$)
            ),
            mergeMap(([action, appsMap, organizationId, transformationMap, versionsMap, allServicesMap]) => {
                const app = appsMap?.[action.serviceId] || allServicesMap?.[action.serviceId];
                const active =
                    transformationMap?.[action.restApiName]?.transformations?.activeTransformations?.[
                        action.transformationType?.toLowerCase()
                    ];
                if (JSON.stringify(active) === JSON.stringify(action.activeTrNames)) {
                    return [];
                }
                if (!versionsMap?.[action.serviceId]?.metadataId) {
                    this.store$.dispatch(
                        GetAssetWithLatestVersion({
                            serviceId: action.serviceId,
                        })
                    );
                    setTimeout(() => {
                        this.store$.dispatch(UpdateActiveTransformationsByType(action));
                    }, 500);
                    return [];
                }
                return from(
                    this._api.assets.updateActiveTransformationsByType({
                        transformationType: action.transformationType,
                        activeTrNames: action.activeTrNames,
                        assetRestAPIName: app?.restApiName,
                        noAlerts: action?.noAlerts,
                    })
                ).pipe(
                    map(() => {
                        this.store$.dispatch(
                            AlertSuccess({
                                message: action.message
                                    ? translate(action.message)
                                    : translate('Transformation activated / deactivated successfully'),
                            })
                        );
                        CommonUtilsService.removeFromStorage('undefined_transformations');
                        this.store$.dispatch(
                            GetInboundDetailedTransformations({
                                serviceId: action.serviceId,
                                assetMetaUid: app?.assetMetaUId || versionsMap?.[action.serviceId]?.metadataId,
                                restApiName: action.restApiName,
                                forceFetch: true,
                                noAlerts: true,
                            })
                        );
                        return SetActiveTransformationsByType({
                            restApiName: action.restApiName,
                            organizationId,
                            transformationNames: action.activeTrNames,
                            transformationType: action.transformationType,
                        });
                    }),
                    catchError(e => of(AlertError({ message: e.msg })))
                );
            })
        )
    );

    getMasterSchedulerTransformations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetMasterSchedulerTransformations),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$), this.store$.select(getAssetsWithLatestVersion$)),
            mergeMap(([action, organizationId, version]) => {
                return from(
                    this._taxilla.transformations.getAssetToAssetChains({
                        assetId: action?.assetId || version?.[action.serviceId]?.metadataId,
                        assetName: action?.assetName,
                        noAlerts: action?.noAlerts,
                    })
                ).pipe(
                    map((res: any) => {
                        const allTransformations: { INBOUND: Chain[]; OUTBOUND: Chain[] } = {
                            INBOUND: (res?.response?.chains?.['INBOUND'] as Chain[]) || [],
                            OUTBOUND: (res?.response?.chains?.['OUTBOUND'] as Chain[]) || [],
                        };
                        return SetMasterSchedulerTransformations({
                            restApiName: action.restApiName,
                            organizationId,
                            transformations: allTransformations,
                        });
                    }),
                    catchError(e => {
                        return of(AlertError({ message: e.msg }));
                    })
                );
            })
        )
    );

    getIntegrationsChains$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetIntegrationsChains),
            withLatestFrom(
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getOutboundIntegrationsChains$),
                this.store$.select(getAssetsWithLatestVersion$)
            ),
            mergeMap(([action, organizationId, chains, versionsMap]) => {
                if (
                    chains.length > 0 ||
                    this.loadingChains[
                        `${action.serviceId}|${action.assetId || versionsMap?.[action.serviceId]?.metadataId}|${
                            action.assetName || versionsMap?.[action.serviceId]?.metadataName
                        }`
                    ]
                ) {
                    return [];
                }
                if (!versionsMap?.[action.serviceId]?.metadataId) {
                    this.store$.dispatch(
                        GetAssetWithLatestVersion({
                            serviceId: action.serviceId,
                        })
                    );
                    setTimeout(() => {
                        this.store$.dispatch(GetIntegrationsChains(action));
                    }, 500);
                    return [];
                }
                const updatedAction = {
                    ...action,
                    assetId: action.assetId || versionsMap?.[action.serviceId]?.metadataId,
                    assetName: action.assetName || versionsMap?.[action.serviceId]?.metadataName,
                };
                this.loadingChains[`${action.serviceId}|${updatedAction.assetId}|${updatedAction.assetName}`] = true;
                return this._taxilla.transformations.getChains(updatedAction).pipe(
                    map((res: any) => {
                        this.loadingChains[`${action.serviceId}|${updatedAction.assetId}|${updatedAction.assetName}`] = false;
                        const chains: { INBOUND: Chain[]; OUTBOUND: Chain[] } = {
                            INBOUND: (res?.response?.chains?.['INBOUND'] as Chain[]) || [],
                            OUTBOUND: (res?.response?.chains?.['OUTBOUND'] as Chain[]) || [],
                        };

                        return SetIntegrationsChains({
                            restApiName: action.restApiName,
                            organizationId,
                            chains,
                        });
                    })
                );
            })
        )
    );

    getOutboundChains$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetOutboundChains),
            withLatestFrom(
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getSubscribedAppsMap$),
                this.store$.select(getCurrentLocationIdsHierarchy$),
                this.store$.select(getAllTransformationsServicesMap$),
                this.store$.select(getAssetsWithLatestVersion$),
                this.store$.select(getAllServicesMap$)
            ),
            mergeMap(([action, organizationId, appsMap, providerIds, transformationsMap, versionsMap, allServicesMap]) => {
                const app = appsMap?.[action.serviceId] || allServicesMap?.[action.serviceId];
                const chains = transformationsMap?.[action.restApiName]?.transformations?.chains;
                if (chains?.length > 0 || !app?.restApiName) {
                    return [];
                }
                if (!versionsMap?.[action.serviceId]?.metadataId) {
                    this.store$.dispatch(
                        GetAssetWithLatestVersion({
                            serviceId: action.serviceId,
                        })
                    );
                    setTimeout(() => {
                        this.store$.dispatch(GetOutboundChains(action));
                    }, 500);
                    return [];
                }
                return this._taxilla.instances
                    .getTransformationChains(app?.assetMetaUId || versionsMap?.[action.serviceId]?.metadataId)
                    .pipe(
                        map((res: any) => {
                            const organizations = res.organizations;
                            const repoIdVsTransformations = res.repoIdVsTransformations;
                            if (Object.keys(res.repoIdVsTransformations).length === 0) {
                                return AlertError({ message: translate('No Excel/CSV transformations exists for the asset') });
                            }
                            const transformationsData = [];
                            const providerOrgId = this._commonUtils.getFromStorage('currentOrganization')?.providerOrganizationId;
                            for (const orgId in repoIdVsTransformations) {
                                if (repoIdVsTransformations.hasOwnProperty(orgId)) {
                                    const newTransformationsGroup = {
                                        orgId: '',
                                        orgName: '',
                                        values: [],
                                    };
                                    newTransformationsGroup.orgId = orgId;
                                    newTransformationsGroup.orgName =
                                        providerOrgId && orgId === providerOrgId ? 'Default' : organizations[orgId];
                                    newTransformationsGroup.values = repoIdVsTransformations[orgId];
                                    transformationsData.push(newTransformationsGroup);
                                }
                            }
                            const chains = this.changeTransformationsHierarchy(providerIds, transformationsData);
                            return SetOutboundChains({
                                restApiName: action.restApiName,
                                chains,
                                organizationId,
                            });
                        }),
                        catchError(() => [])
                    );
            })
        )
    );

    getTransformationNamesInChain = createEffect(() =>
        this.actions$.pipe(
            ofType(GetTransformationNamesInChain),
            withLatestFrom(
                this.store$.select(getAllTransformationsServicesMap$),
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getAssetsWithLatestVersion$)
            ),
            mergeMap(([action, transformationsMap, orgId, versionsMap]) => {
                const transformationNames = transformationsMap?.[action.serviceId]?.transformationsByChainName?.[action.chainName];
                if (transformationNames?.length > 0) {
                    return [];
                }
                if (!versionsMap?.[action.serviceId]?.metadataId) {
                    this.store$.dispatch(
                        GetAssetWithLatestVersion({
                            serviceId: action.serviceId,
                        })
                    );
                    setTimeout(() => {
                        this.store$.dispatch(GetTransformationNamesInChain(action));
                    }, 500);
                    return [];
                }
                return from(
                    this._taxilla.transformations.getTransformationNamesInChain({
                        repositoryId: action.repositoryId,
                        chainName: action.chainName,
                        assetName: action.assetName,
                        assetId: action.assetId || versionsMap?.[action.serviceId]?.metadataId,
                    })
                ).pipe(
                    map((res: any) => {
                        return SetTransformationNamesInChain({
                            organizationId: orgId,
                            restApiName: action.restApiName,
                            chainName: action.chainName,
                            transformations: res,
                        });
                    }),
                    catchError(e => of(AlertError({ message: e.msg })))
                );
            })
        )
    );

    private changeTransformationsHierarchy = (providerIds: string[], transformations) => {
        const downloadReportTransformations = [];
        providerIds.forEach(orgId => {
            transformations.find((object, index) => {
                if (object.orgId === orgId) {
                    downloadReportTransformations.push(transformations[index]);
                }
            });
        });
        return downloadReportTransformations;
    };

    getChainsAndTransformations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetChainsAndTransformation),
            withLatestFrom(
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getAllTransformationsServicesMap$),
                this.store$.select(getServicesApiMap$)
            ),
            mergeMap(([action, orgId, transformationMap, servicesApiMap]) => {
                const transformations = CommonUtilsService.cloneObject(transformationMap?.[action.restApiName]?.transformations);
                const chainsAndTransformations = transformations?.chainsAndTransformations?.outbound?.[orgId]?.length || [];
                if (chainsAndTransformations > 1) {
                    return [];
                }
                const app = servicesApiMap?.[action.restApiName];
                if (!app?.serviceId) {
                    this.store$.dispatch(GetAppDetailsThroughApi({ apiName: action.restApiName }));
                    setTimeout(() => {
                        this.store$.dispatch(GetChainsAndTransformation(action));
                    }, 1000);
                    return [];
                }
                return from(this._taxilla.transformations.getChainsAndTransformations({ serviceId: app.serviceId })).pipe(
                    map((res: any) => {
                        const chains: { inbound: Chain[]; outbound: Chain[] } = {
                            inbound: (res?.response?.INBOUND as Chain[]) || [],
                            outbound: (res?.response?.OUTBOUND as Chain[]) || [],
                        };
                        return SetTransformationAndChains({
                            restApiName: action.restApiName,
                            organizationId: orgId,
                            chains,
                        });
                    })
                );
            })
        )
    );
}
